import _escapeRegExp from 'lodash/escapeRegExp';
import _isArray from 'lodash/isArray';
import _isObject from 'lodash/isObject';
import _keys from 'lodash/keys';

import { isMetadataOnlyNode } from './recipe-utils';
import { getNodeLabel } from './types';
import { typeRequiredKeys, NODE_TYPES } from './types/constants';

export function collectNodes(node, path = []) {
  if (_isArray(node)) {
    return node.reduce(
      (nodes, childNode, index) => [...nodes, ...collectNodes(childNode, [...path, index - 1])],
      [],
    );
  }

  return _keys(node).reduce(
    (nodes, key) => {
      const childNode = node[key];

      if (_isObject(childNode)) {
        return [...nodes, ...collectNodes(childNode, [...path, key])];
      }

      return nodes;
    },
    [{ ...node, __path__: path.join('.') }],
  );
}

export function pipe(...fns) {
  return (x) => fns.reduce((v, f) => f(v), x);
}

function removeMetadataNodes(nodes) {
  return nodes.filter((node) => !isMetadataOnlyNode(node));
}

function removeUnresolvedTypeNodes(nodes) {
  return nodes.filter(({ __type__ }) => __type__ !== NODE_TYPES.UNRESOLVED);
}

function filterByTypes(nodes, types = []) {
  if (types.length === 0) {
    return nodes;
  }

  return nodes.filter(({ __type__ }) => types.includes(__type__));
}

function isMatch(value, query) {
  const re = new RegExp(_escapeRegExp(query), 'i');
  return re.test(value);
}

function nodeIsMatch(node, query) {
  return _keys(node).some((key) => isMatch(key, query) || isMatch(node[key], query));
}

function findNodeMatches(nodes, query) {
  return nodes.filter((node) => nodeIsMatch(node, query));
}

export function stripObjectLikeFields(node) {
  if (!_isObject(node)) {
    return node;
  }

  return _keys(node).reduce((acc, key) => {
    const childNode = node[key];

    if (_isObject(childNode)) {
      return acc;
    }

    return { ...acc, [key]: childNode };
  }, {});
}

export function searchNodes(node, query, typeFilters = _keys(typeRequiredKeys)) {
  const allNodes = collectNodes(node);
  return pipe(
    removeMetadataNodes,
    removeUnresolvedTypeNodes,
    (nodes) => filterByTypes(nodes, typeFilters),
    (nodes) =>
      nodes.map((currentNode) => ({ ...currentNode, __label__: getNodeLabel(currentNode) })),
    (nodes) => nodes.map((currentNode) => stripObjectLikeFields(currentNode)),
    (nodes) => findNodeMatches(nodes, query),
  )(allNodes);
}
